<template>
  <div class="home-section-wrapper">
    <div class="title-container">
      <h2 class="title">{{ $t(title) }}</h2>
      <p v-if="subtitle" class="subtitle">{{ $t(subtitle) }}</p>
    </div>

    <div class="content-wrapper">
      <div class="slot-wrapper">
        <slot></slot>
      </div>
      <div v-if="previewSrc" class="preview-video-container">
        <p v-if="!$device.isMobile" class="how-it-works">{{ $t('homePageHowItWorks') }}</p>
        <el-skeleton :loading="!assetsLoaded" animated>
          <template #template>
            <el-skeleton-item
              :style="{
                width: '100%',
                left: 0,
                aspectRatio: $device.isMobile ? '9 / 16' : '16 / 9',
                height: '100%',
                position: 'absolute'
              }"
            />
          </template>
        </el-skeleton>
        <video ref="refVideo" :src="previewSrc" loop muted playsinline preload="auto" />
      </div>
    </div>
    <div v-if="explainSrc" class="more-info-container">
      <div class="more-info-max-width">
        <button class="more-info-button" @click="showPopup = true">
          <span>{{ $t('homePageLearnMoreBtnText') }}</span>
          <img src="/img/icons/play.svg" alt="" width="14" />
        </button>
      </div>
    </div>

    <el-dialog
      v-model="showPopup"
      :fullscreen="$device.isMobile"
      :append-to-body="true"
      class="video-explain-popup"
      @open="popupVideoElement?.play()"
      @close="popupVideoElement?.pause()"
    >
      <div class="video-wrapper">
        <video ref="popupVideoElement" :src="explainSrc" controls />
      </div>
    </el-dialog>
  </div>
</template>
<script lang="ts" setup>
const props = defineProps<{
  title: string;
  subtitle?: string;
  previewSrc?: string;
  explainSrc?: string;
  assetsLoaded?: boolean;
}>();

const refVideo = ref<HTMLVideoElement>();
const popupVideoElement = ref<HTMLVideoElement>();
const videoElementLoaded = ref(false);

onMounted(() => {
  if (!refVideo.value) return;
  refVideo.value.load();
  refVideo.value.oncanplaythrough = () => {
    videoElementLoaded.value = true;
    refVideo.value?.play();
  };
});

watch(
  () => props.explainSrc,
  () => {
    popupVideoElement.value?.load();
  }
);

const showPopup = ref(false);
</script>
<style lang="scss" scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.home-section-wrapper {
  padding-top: 70px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  color: white;
  font-family: Grenze, sans-serif;

  @media screen and (max-width: 767px) {
    padding-top: 40px;
  }
}

.title-container {
  margin-bottom: 40px;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media screen and (max-width: 479px) {
    gap: 15px;
    padding: 0 40px;
  }
}

.title {
  font-weight: 700;
  font-size: 48px;
  text-align: center;
  line-height: 45px;

  @media screen and (max-width: 479px) {
    hyphens: auto;
    font-size: 36px;
    line-height: 1.05;
    margin: 0;
  }
}

.subtitle {
  font-weight: 400;
  font-size: 24px;
  text-align: center;
  line-height: 1;
  color: #c2c2c2;

  @media screen and (max-width: 479px) {
    font-size: 16px;
  }
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: var(--main-container-max-width);

  .slot-wrapper {
    order: 1;
  }

  .preview-video-container {
    order: 2;
  }

  @media screen and (max-width: 479px) {
    .slot-wrapper {
      order: 2;
    }

    .preview-video-container {
      order: 1;
    }
  }
}

.preview-video-container {
  border: 1px solid #1e353e;
  border-radius: 20px;
  overflow: hidden;
  background: #03252f;
  padding: 20px;
  position: relative;

  @media screen and (max-width: 479px) {
    padding: 0;
    border: none;
  }
}

.preview-video-container video {
  width: 100%;
  aspect-ratio: 16 / 9;

  @media screen and (max-width: 479px) {
    aspect-ratio: 9 / 16;
  }
}

.how-it-works {
  font-family: PT Serif;
  font-weight: 400;
  font-size: 20px;
  color: #c2c2c2;
}

.more-info-max-width {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding-right: 32px;

  @media screen and (max-width: 479px) {
    justify-content: center;
    padding-right: 0;
  }
}

.more-info-container {
  margin-top: 25px;
  width: 100%;
  height: 63px;
  background-image: linear-gradient(to top, #081b25bf 0%, #081b2580 15%, #081b2540 30%, #081b2500 80%);
  border-bottom: 1px solid #1e353e;

  @media screen and (max-width: 479px) {
    margin-top: 10px;
  }
}

.video-wrapper {
  height: 100%;
}

.video-wrapper video {
  aspect-ratio: 16 / 9;
  width: 100%;

  @media screen and (max-width: 479px) {
    aspect-ratio: 9 / 16;
    height: 100%;
    object-fit: cover;
  }
}

.more-info-button {
  background-color: #031f2a;
  border: 2px solid #1e353e;
  border-radius: 9px;
  padding: 8px 25px;
  font-size: 20px;
  color: #838383;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.7s ease;
  img {
    transition: all 0.7s ease;
  }

  @media (hover: hover) {
    &:hover {
      border-color: #838383;
      img {
        width: 24px;
      }
    }
  }
}
</style>

<style lang="scss">
.video-explain-popup.el-dialog {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  max-width: 1250px;
  width: 100%;
  max-height: 95%;
  overflow: hidden;

  .el-dialog__body {
    max-width: 100%;
    max-height: 100%;
    height: 100%;
  }
}
</style>
